import { RichTextRenderer } from "@scott/contentful/rich-text-renderer";
import type {
  ContentfulButton,
  ContentfulHeadlineBlock,
  ContentfulTextBlock,
} from "@scott/contentful/types";
import { cva, type VariantProps } from "class-variance-authority";
import { Button } from "../../elements/button/button";
import { TextD2C } from "../../elements/text-d2c/text";
import { IconChevronRight } from "../../icons";
import { Column, Container, Row } from "../../layout/grid";
import { cn } from "../../lib/utils";
import type { TextImageInspectorProps } from "./text-image.preview";

const textImageVariants = cva("", {
  variants: {
    backgroundColor: {
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
      black: "bg-black",
    },
    orderReverse: {
      true: "",
      false: "",
    },
  },
  defaultVariants: {
    backgroundColor: "white",
    orderReverse: false,
  },
});

const WIDE_COLUMN_WIDTH = 12;

interface ColumnStartClasses {
  8: string;
  10: string;
  12: string;
}

function createColumnVariants(
  normalStart: ColumnStartClasses,
  reversedStart: ColumnStartClasses
) {
  return cva("", {
    variants: {
      columnWidth: {
        8: "col-span-12 md:col-span-4 md:row-start-1 lg:col-span-4 lg:row-start-1",
        10: "col-span-12 md:col-span-5 md:row-start-1 lg:col-span-5 lg:row-start-1",
        12: "col-span-12 md:col-span-6 md:row-start-1 lg:col-span-6 lg:row-start-1",
      },
      orderReverse: { true: "", false: "" },
    },
    compoundVariants: [
      { columnWidth: 8, orderReverse: false, className: normalStart[8] },
      { columnWidth: 10, orderReverse: false, className: normalStart[10] },
      { columnWidth: 12, orderReverse: false, className: normalStart[12] },
      { columnWidth: 8, orderReverse: true, className: reversedStart[8] },
      { columnWidth: 10, orderReverse: true, className: reversedStart[10] },
      { columnWidth: 12, orderReverse: true, className: reversedStart[12] },
    ],
    defaultVariants: {
      columnWidth: 10,
      orderReverse: false,
    },
  });
}

const textColumnVariants = createColumnVariants(
  {
    8: "md:col-start-3 lg:col-start-3",
    10: "md:col-start-2 lg:col-start-2",
    12: "md:col-start-1 lg:col-start-1",
  },
  {
    8: "md:col-start-7 lg:col-start-7",
    10: "md:col-start-7 lg:col-start-7",
    12: "md:col-start-7 lg:col-start-7",
  }
);

const imageColumnVariants = createColumnVariants(
  {
    8: "md:col-start-7 lg:col-start-7",
    10: "md:col-start-7 lg:col-start-7",
    12: "md:col-start-7 lg:col-start-7",
  },
  {
    8: "md:col-start-3 lg:col-start-3",
    10: "md:col-start-2 lg:col-start-2",
    12: "md:col-start-1 lg:col-start-1",
  }
);

export interface TextImageProps extends VariantProps<typeof textImageVariants> {
  sys?: { id: string };
  backgroundColor?: "black" | "white" | "whitesmoke";
  headlineColor?: "black" | "white" | "alabastergrey";
  color?: "black" | "white" | "alabastergrey";
  columnWidth?: 8 | 10 | 12;
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  embedded?: boolean;
  orderReverse?: boolean;
  headline?: ContentfulHeadlineBlock;
  text?: ContentfulTextBlock;
  image?: {
    sys?: { id: string };
    src?: string;
    srcSet?: string;
    className?: string;
    alt?: string;
  };
  cta?: ContentfulButton | null;
  inspectorProps?: TextImageInspectorProps;
}

export function TextImage({
  backgroundColor,
  headlineColor,
  color,
  columnWidth = 10,
  orderReverse,
  headline,
  text,
  image,
  cta,
  paddingTop = "medium",
  paddingBottom = "medium",
  embedded = false,
  inspectorProps,
}: Readonly<TextImageProps>) {
  const resolvedColumnWidth = embedded ? WIDE_COLUMN_WIDTH : columnWidth;
  const variantClasses = textImageVariants({
    backgroundColor,
    orderReverse,
  });

  const textColumnClasses = textColumnVariants({
    columnWidth: resolvedColumnWidth,
    orderReverse,
  });

  const buttonClasses =
    backgroundColor === "black"
      ? "text-white bg-transparent"
      : "text-black bg-transparent";

  const imageColumnClasses = imageColumnVariants({
    columnWidth: resolvedColumnWidth,
    orderReverse,
  });

  const content = (
    <Row className="" {...inspectorProps?.container}>
      <Column className={cn("content-center", textColumnClasses)}>
        {headline && (
          <TextD2C
            color={headlineColor}
            dash={true}
            variant={"h3"}
            {...inspectorProps?.headline}
          >
            <RichTextRenderer richText={headline.headline?.json} />
          </TextD2C>
        )}

        <TextD2C color={color} variant={"p"} {...inspectorProps?.text}>
          <RichTextRenderer richText={text?.text?.json} />
        </TextD2C>

        {cta?.href && (
          <Button
            className={buttonClasses}
            href={cta.href}
            mode="D2C"
            size="default"
            target={cta.target}
            variant="outline-no-hover"
          >
            {cta.label} <IconChevronRight />
          </Button>
        )}
      </Column>
      <Column className={cn("content-center", imageColumnClasses)}>
        <img
          alt={image?.alt || "Scott image alt text"}
          className={cn(
            image?.className ||
              "mb-6 aspect-square w-full object-cover object-center md:mb-0"
          )}
          loading="lazy"
          sizes={image?.srcSet ? "(min-width: 768px) 50vw, 100vw" : undefined}
          src={image?.src || "/assets/placeholder.svg"}
          srcSet={image?.srcSet}
          {...inspectorProps?.image}
        />
      </Column>
    </Row>
  );

  if (embedded) {
    return (
      <div className="component-text-image h-full" data-embedded="true">
        {content}
      </div>
    );
  }

  return (
    <Container
      className={cn(variantClasses, "component-text-image -mb-px")}
      fullWidth={true}
      paddingBottom={paddingBottom}
      paddingTop={paddingTop}
    >
      {content}
    </Container>
  );
}
